import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Button, Card, Divider, Link, Stack, styled, SxProps, Typography } from "@mui/material";
import { FC, useContext, useState } from "react";
import { cacheService, workflowService } from "../../../Core/Api/services";
import { AdminAssetSinglePendingUploadDetails } from "../../../Core/Api/Types/db-asset-object-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { ModalContext } from "../../Modals";
import ModalCompleteUserTask, {
  ModalCompleteUserTaskElementProps,
} from "../../Modals/ModalCompleteUserTask/modal-complete-user-task";
import { useTranslation } from "react-i18next";

export interface AssetHeroPendingQcCardProps {
  sx?: SxProps;
  qcDetails: AdminAssetSinglePendingUploadDetails;
}

const AssetHeroPendingQcCard: FC<AssetHeroPendingQcCardProps> = (props) => {
  const { t } = useTranslation();
  const { sx = {}, qcDetails } = props;
  const {
    createdBy,
    startDate,
    currentTaskName,
    isClaimedByUser,
    userHasRightToClaim,
    userHasRightToComplete,
    workflowInstanceId,
    task,
  } = qcDetails;

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();

  const [claimed, setClaimed] = useState(isClaimedByUser);
  const [claimedUserName, setClaimedUserName] = useState(task.claimInfos?.userName ?? null);

  const openModalCompleteTask = () => {
    if (!openModal) return;
    openModal<ModalCompleteUserTaskElementProps>(ModalCompleteUserTask, { task, workflowId: workflowInstanceId });
  };

  const claimUserTask = () => {
    workflowService.ClaimUserTask(workflowInstanceId).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.claimedWorkflowUserTask"), "success");
        setClaimed(true);
        setClaimedUserName(cacheService.GetUserInfo()?.name ?? null);
      } else enqueueCustomSnackbar(t("alert:error.tryingClaimTask"), "warning");
    });
  };

  const unclaimUserTask = () => {
    workflowService.UnclaimUserTask(workflowInstanceId).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.unclaimedWorkflowUserTask"), "success");
        setClaimed(false);
        setClaimedUserName(null);
      } else enqueueCustomSnackbar(t("alert:error.tryingUnclaimTask"), "warning");
    });
  };

  return (
    <StyledCard variant="outlined" sx={sx}>
      <Stack direction="row" justifyContent="space-between">
        <MainContent justifyContent="space-between" spacing={2}>
          <div>
            <Typography fontWeight={600} variant="subtitle2">
              {t("component:asset.body.qualityCheckDetails")}
            </Typography>
          </div>
          <Stack spacing={1}>
            <MetadataListItem title={t("common:createdBy")}>
              {createdBy !== "System" ? (
                <Link href="#" underline="hover" fontWeight={500}>
                  {createdBy}
                </Link>
              ) : (
                createdBy
              )}
            </MetadataListItem>
            <MetadataListTextItem title={t("menu:title.startedAt")}>{startDate}</MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.currentTaskName")}>{currentTaskName}</MetadataListTextItem>
          </Stack>
        </MainContent>
        <Divider orientation="vertical" sx={{ mr: 5, ml: 5, height: "auto" }} />
        <Actions>
          <Button
            sx={{ mb: 1 }}
            size="small"
            variant="contained"
            disabled={!userHasRightToClaim}
            startIcon={<LockOutlinedIcon />}
            onClick={claimUserTask}
          >
            {claimed ? (task.claimInfos?.userId === cacheService.GetUserInfo()?.id ? t("common:you") : claimedUserName) : t("common:claim")}
          </Button>
          {claimed && (
            <Button
              sx={{ mb: 1 }}
              size="small"
              variant="contained"
              disabled={!userHasRightToClaim}
              startIcon={<LockOpenIcon />}
              onClick={unclaimUserTask}
            >
              {t("common:unclaim")}
            </Button>
          )}
          <Button
            sx={{ mb: 1 }}
            size="small"
            disabled={!claimed || !userHasRightToComplete}
            startIcon={<CheckCircleOutlineOutlinedIcon />}
            onClick={openModalCompleteTask}
          >
            {t("common:complete")}
          </Button>
        </Actions>
      </Stack>
    </StyledCard>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  padding: `${theme.spacing(3)} ${theme.spacing(5)} ${theme.spacing(3)} ${theme.spacing(3)}`,
  background: "linear-gradient(180deg, rgba(252, 164, 100, 0.05) 0%, rgba(252, 164, 100, 0) 100%)",
}));

const MainContent = styled(Stack)(() => ({
  flex: "1 1 auto",
}));

const Actions = styled(Stack)(() => ({
  width: "240px",
  flex: "0 0 auto",
}));

export default AssetHeroPendingQcCard;
